import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  QButton,
  QLookup,
  QModalHeader,
  useCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';

import { documentApi } from '../../../../../../api/document';
import {
  DocumentCategory,
  DocumentStatus,
  GroupUser,
  QualioDocumentAuditGroups,
  QualioDocumentAuditGroupsPatch,
  QualioDocumentPatch,
} from '../../../../../../api/model/document';
import { Group } from '../../../../../../api/model/group';
import { Tag } from '../../../../../../api/model/tag';
import { MedtechUserV2 } from '../../../../../../api/user';

import { DocumentOverviewContext } from '../../../Context';
import { shouldRenderManageReviewersButton } from '../../../RenderLogic';

import { AxiosError } from 'axios';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { groupUserToMedtechUser } from '../../../../../../util/UserUtils';
import { QLookupModalComposedHeader } from '../../../../QLookupModalComposedHeader';
import {
  canUserAccessAtLeastOnePrivateTag,
  filterUserOptionLookup,
  getDocumentTagIdsToGroupIdsMap,
  getUserLookupView,
} from '../../utils';
import {
  ManageReviewersConfirmationModal,
  ManageReviewersConfirmationModalProps,
} from './ManageReviewersConfirmationModal/ManageReviewersConfirmationModal';

const mapToId = ({ id }: { id: number }) => id;

export type ManageReviewersButtonProps = {
  tags: Tag[];
  groups: Group[];
  users: MedtechUserV2[];
  currentReviewers: QualioDocumentAuditGroups['review_group'];
};

export const ManageReviewersButton = ({
  tags,
  groups,
  users,
  currentReviewers = [],
}: ManageReviewersButtonProps) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { showToast } = useToastProvider();
  const [isLookupModalOpen, setIsLookupModalOpen] = useState(false);
  const { qePricingAndPackaging2024 } = useFlags();

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationModalProps, setConfirmationModalProps] =
    useState<
      Pick<
        ManageReviewersConfirmationModalProps,
        | 'addedReviewers'
        | 'removedReviewers'
        | 'selections'
        | 'onSubmit'
        | 'requiresComment'
      >
    >();

  const onClose = useCallback(() => {
    setIsLookupModalOpen(false);
    setIsConfirmationModalOpen(false);
    setConfirmationModalProps(undefined);
  }, []);

  const commonToastProps = useMemo(() => {
    return {
      id: 'manage-reviewers-property-toast',
      replace: true,
    };
  }, []);

  const { qualioDocument, refetchDocumentOverviewData } = useContext(
    DocumentOverviewContext,
  );

  const onSuccess = useCallback(() => {
    refetchDocumentOverviewData();
    showToast({
      'data-cy': 'manage-reviewers-button-notify-success-toast',
      ...commonToastProps,
      status: 'success',
      title: t('notifications.reviewersAssigned'),
      description: t('notifications.successfullyChangedReviewers'),
    });
    onClose();
  }, [commonToastProps, onClose, refetchDocumentOverviewData, showToast, t]);

  const onError = useCallback(
    (error?: string) => {
      const message = error
        ? t('errors.failedToApplyChangesToReviewers', { error: `${error}.` })
        : t('errors.failedToApplyChangesToReviewers', {
            error: 'Please try again.',
          });

      showToast({
        'data-cy': 'manage-reviewers-button-notify-error-toast',
        ...commonToastProps,
        status: 'error',
        title: t('notifications.error'),
        description: message,
      });
    },
    [commonToastProps, showToast, t],
  );

  const canManageReviewers = useMemo(
    () => shouldRenderManageReviewersButton(qualioDocument, currentUser),
    [currentUser, qualioDocument],
  );

  const currentReviewersMap = useMemo(
    () =>
      currentReviewers.reduce(
        (acc: Map<GroupUser['id'], GroupUser>, Reviewer) => {
          return acc.set(Reviewer.id, Reviewer);
        },
        new Map(),
      ),
    [currentReviewers],
  );

  const documentTagIdsToGroupIdsMap = useMemo(
    () => getDocumentTagIdsToGroupIdsMap(qualioDocument, tags),
    [qualioDocument, tags],
  );

  const validReviewers = useMemo(
    () =>
      users.filter((user) => {
        const { full_name, permissions, id } = user;
        const permissionToReview = (permissions: string[]) => {
          if (!qePricingAndPackaging2024) {
            return permissions.includes('can_review_doc');
          }
          return (
            (permissions.includes('can_review_doc') &&
              qualioDocument.category === DocumentCategory.REGULAR) ||
            (permissions.includes('can_work_on_issue') &&
              qualioDocument.category === DocumentCategory.ISSUE_RELATED)
          );
        };
        const isValidDocumentReviewer =
          !!full_name &&
          (currentReviewersMap.has(id) ||
            ((documentTagIdsToGroupIdsMap.size === 0 ||
              canUserAccessAtLeastOnePrivateTag(
                user,
                Array.from(documentTagIdsToGroupIdsMap.values()),
              )) &&
              permissionToReview(permissions) &&
              id !== qualioDocument.owner.id));
        return isValidDocumentReviewer;
      }),
    [
      documentTagIdsToGroupIdsMap,
      users,
      qualioDocument.category,
      qePricingAndPackaging2024,
      currentReviewersMap,
      qualioDocument.owner,
    ],
  );

  // In case an reviewer has been deleted, we need to display it to be deselected
  const deletedReviewers: MedtechUserV2[] = useMemo(
    () =>
      currentReviewers
        .filter(
          (reviewer) => !validReviewers.find((vr) => vr.id === reviewer.id),
        )
        .map((deletedReviewer) => groupUserToMedtechUser(deletedReviewer)),
    [currentReviewers, validReviewers],
  );

  const reviewersPool = useMemo(
    () => [...validReviewers, ...deletedReviewers],
    [deletedReviewers, validReviewers],
  );

  const userDataView = useMemo(() => getUserLookupView(groups, t), [groups, t]);

  const filterOptionCallback = useCallback(
    (user: MedtechUserV2, searchTerm: string | undefined) => {
      if (!searchTerm) {
        return true;
      }
      return filterUserOptionLookup(searchTerm, user, groups);
    },
    [groups],
  );

  const isReviewerRowDisabled = useCallback(
    (reviewer: MedtechUserV2) => {
      const currentReviewer = currentReviewersMap.get(reviewer.id);
      const isReviewerActionDone = currentReviewer?.action_done;
      return !!isReviewerActionDone;
    },
    [currentReviewersMap],
  );

  const isCurrentReviewer = useCallback(
    (user: Pick<MedtechUserV2, 'id'>) => currentReviewersMap.has(user.id),
    [currentReviewersMap],
  );

  const buildAuditGroupPatch = useCallback(
    (reviewers: number[]): QualioDocumentPatch => {
      const auditGroupPatch: QualioDocumentAuditGroupsPatch = {
        review_group: reviewers,
        approver_group: (qualioDocument.audit_groups?.approver_group ?? []).map(
          mapToId,
        ),
      };
      return {
        audit_groups: auditGroupPatch,
      };
    },
    [qualioDocument],
  );

  const getReviewerChanges = useCallback(
    (selections: readonly MedtechUserV2[]) => {
      const selectedReviewerIds = selections.map(mapToId);
      const addedReviewers = selections.filter(
        (user) => !isCurrentReviewer(user),
      );
      const removedReviewers = currentReviewers.filter(
        (user) => !selectedReviewerIds.includes(user.id),
      );
      return {
        addedReviewers,
        removedReviewers,
        selectedReviewerIds,
      };
    },
    [currentReviewers, isCurrentReviewer],
  );

  const applyReviewerChanges = useCallback(
    async (selections: readonly MedtechUserV2[]) => {
      const selectedReviewerIds = selections.map(mapToId);
      const documentPatch: QualioDocumentPatch =
        buildAuditGroupPatch(selectedReviewerIds);
      try {
        await documentApi.update(documentPatch, qualioDocument.id, true);
        onSuccess();
      } catch (error) {
        if (error instanceof AxiosError) {
          onError(error.response?.data.message);
        } else {
          onError();
        }
      }
    },
    [buildAuditGroupPatch, onError, onSuccess, qualioDocument],
  );

  const applyReviewerChangesForNonDraft = useCallback(
    async (selections: readonly MedtechUserV2[], comment: string) => {
      const { addedReviewers, removedReviewers } =
        getReviewerChanges(selections);
      try {
        await documentApi.updateDocumentReviewers(
          currentUser.companyId,
          qualioDocument.id,
          {
            addedUsercompanyIds: addedReviewers.map(
              ({ usercompany_id }) => usercompany_id,
            ),
            removedUsercompanyIds: removedReviewers.map(
              ({ usercompany_id }) => usercompany_id,
            ),
            comment,
          },
        );
        onSuccess();
      } catch (error) {
        if (error instanceof AxiosError) {
          onError(error.response?.data.message);
        } else {
          onError();
        }
      }
    },
    [currentUser, getReviewerChanges, onError, onSuccess, qualioDocument],
  );

  const updateReviewers = useCallback(
    async (selections: readonly MedtechUserV2[]) => {
      const { addedReviewers, removedReviewers } =
        getReviewerChanges(selections);

      if (qualioDocument.status_id === DocumentStatus.Draft) {
        setConfirmationModalProps({
          addedReviewers,
          removedReviewers,
          selections,
          onSubmit: (selections) => applyReviewerChanges(selections),
        });
      } else {
        setConfirmationModalProps({
          addedReviewers,
          removedReviewers,
          selections,
          requiresComment: removedReviewers.length > 0,
          onSubmit: (selections, comment) =>
            applyReviewerChangesForNonDraft(selections, comment),
        });
      }

      setIsLookupModalOpen(false);
      setIsConfirmationModalOpen(true);
      return Promise.reject(new Error());
    },
    [
      applyReviewerChanges,
      applyReviewerChangesForNonDraft,
      getReviewerChanges,
      qualioDocument,
    ],
  );

  if (!canManageReviewers) {
    return null;
  }

  const showModal = isLookupModalOpen || isConfirmationModalOpen;

  return (
    <>
      <QButton
        data-cy="document-manage-reviewers-button"
        variant="ghost"
        onClick={() => setIsLookupModalOpen(true)}
      >
        {t('buttons.manage')}
      </QButton>
      {showModal && (
        <>
          <QLookup.DataProvider.Fixed
            data={reviewersPool}
            filterOption={filterOptionCallback}
          >
            <QLookup.MultiSelect<MedtechUserV2>
              isOpen={isLookupModalOpen}
              action={t('buttons.continue')}
              searchPlaceholder={t('forms.filterByNameEmailOrGroupName')}
              accessors={{
                id: 'id',
              }}
              isItemDisabled={isReviewerRowDisabled}
              isItemPreSelected={isCurrentReviewer}
              onSelect={updateReviewers}
              onCancel={onClose}
              view={userDataView}
              defaultSortBy={{ id: 'full_name' }}
            >
              <QModalHeader>
                <QLookupModalComposedHeader
                  title={t('modals.manageReviewers')}
                  subtitle={t('messages.selectUsersToProvideFeedback')}
                />
              </QModalHeader>
            </QLookup.MultiSelect>
          </QLookup.DataProvider.Fixed>
          <ManageReviewersConfirmationModal
            {...confirmationModalProps!}
            isOpen={isConfirmationModalOpen}
            onClose={onClose}
            onPrevious={() => {
              setIsLookupModalOpen(true);
              setIsConfirmationModalOpen(false);
            }}
            modalTitle={t('modals.manageReviewers')}
          />
        </>
      )}
    </>
  );
};
